
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Activity, TrendingUp, Link } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import { useIsMobile } from '@/hooks/use-mobile';

export const EnhancedAccountsPageHeader = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-muted/20 border border-border/50 rounded-xl p-3 md:p-6 mb-4 md:mb-6">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-40 h-40 bg-primary rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-blue-500 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative">
        {/* Header Row */}
        <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
          <Button
            variant="ghost"
            size={isMobile ? "sm" : "icon"}
            onClick={() => navigate('/')}
            className="hover:bg-primary/10 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
          <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
            <div className={`${isMobile ? 'h-10 w-10' : 'h-14 w-14'} overflow-hidden rounded-xl ${isMobile ? 'border' : 'border-2'} border-primary/20 bg-background shadow-sm flex-shrink-0`}>
              <div className={`h-full w-full bg-gradient-to-br from-primary/20 to-primary/30 flex items-center justify-center ${isMobile ? 'p-0.5' : 'p-1'}`}>
                <Link className={`${isMobile ? 'h-5 w-5' : 'h-8 w-8'} text-primary`} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className={`${isMobile ? 'text-lg' : 'text-xl md:text-3xl'} font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent truncate`}>
                {t('exchange_accounts')}
              </h1>
              {!isMobile && (
                <p className="text-xs md:text-sm text-muted-foreground mt-1 line-clamp-2">
                  {t('connect_your_accounts')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards Row */}
        <div className={`grid ${isMobile ? 'grid-cols-2 gap-2' : 'grid-cols-1 md:grid-cols-3 gap-3 md:gap-4'}`}>
          <Card className="bg-gradient-to-r from-background to-background/80 border-border/50 shadow-sm hover:shadow-md transition-shadow">
            <div className={`${isMobile ? 'p-2' : 'p-3 md:p-4'}`}>
              <div className={`flex items-center ${isMobile ? 'flex-col text-center gap-1' : 'gap-3'}`}>
                <div className={`${isMobile ? 'p-1' : 'p-2'} bg-primary/10 rounded-lg flex-shrink-0`}>
                  <Activity className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-primary`} />
                </div>
                <div className={isMobile ? 'flex-1' : ''}>
                  <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground font-medium ${isMobile ? 'truncate' : ''}`}>
                    {isMobile ? 'Platforms' : 'Trading Platforms'}
                  </p>
                  <p className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-foreground`}>3</p>
                </div>
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-r from-background to-background/80 border-border/50 shadow-sm hover:shadow-md transition-shadow">
            <div className={`${isMobile ? 'p-2' : 'p-3 md:p-4'}`}>
              <div className={`flex items-center ${isMobile ? 'flex-col text-center gap-1' : 'gap-3'}`}>
                <div className={`${isMobile ? 'p-1' : 'p-2'} bg-green-100 rounded-lg flex-shrink-0`}>
                  <TrendingUp className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-green-600`} />
                </div>
                <div className={isMobile ? 'flex-1' : ''}>
                  <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground font-medium ${isMobile ? 'truncate' : ''}`}>
                    {isMobile ? 'Exchanges' : 'Crypto Exchanges'}
                  </p>
                  <p className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-foreground`}>5</p>
                </div>
              </div>
            </div>
          </Card>

          {!isMobile && (
            <Card className="bg-gradient-to-r from-background to-background/80 border-border/50 shadow-sm hover:shadow-md transition-shadow">
              <div className="p-3 md:p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                    <Link className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">
                      Available Soon
                    </p>
                    <p className="text-lg font-bold text-foreground">More Coming</p>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
